export type TarEntry = {
	readonly name: string
	readonly content: string
}

const textEncoder = new TextEncoder()
const TAR_BLOCK_SIZE = 512

export function sanitizeFilename(filename: string): string {
	const normalized = filename
		.normalize('NFKD')
		.replace(/[^\w.\-]+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
	const safeName = normalized.length ? normalized : 'sop.md'
	return safeName.endsWith('.md') ? safeName : `${safeName}.md`
}

export function createTarArchive(entries: readonly TarEntry[]): Blob {
	const chunks: BlobPart[] = []

	for (const entry of entries) {
		const header = new Uint8Array(TAR_BLOCK_SIZE)
		const contentBytes = textEncoder.encode(entry.content)
		const size = contentBytes.length
		const sanitizedName = sanitizeFilename(entry.name)
		const now = Math.floor(Date.now() / 1000)

		writeString(header, 0, 100, sanitizedName)
		writeOctal(header, 100, 8, 0o100644)
		writeOctal(header, 108, 8, 0)
		writeOctal(header, 116, 8, 0)
		writeOctal(header, 124, 12, size)
		writeOctal(header, 136, 12, now)

		for (let index = 148; index < 156; index += 1) {
			header[index] = 32
		}

		header[156] = '0'.charCodeAt(0)
		writeString(header, 257, 6, 'ustar\0')
		writeString(header, 263, 2, '00')
		writeString(header, 265, 32, 'user')
		writeString(header, 297, 32, 'group')

		let checksum = 0
		for (let index = 0; index < TAR_BLOCK_SIZE; index += 1) {
			checksum += header[index] ?? 0
		}
		const checksumString = checksum.toString(8).padStart(6, '0')
		writeString(header, 148, 6, checksumString)
		header[154] = 0
		header[155] = 32

		chunks.push(header)
		chunks.push(contentBytes)

		const remainder = size % TAR_BLOCK_SIZE
		if (remainder !== 0) {
			chunks.push(new Uint8Array(TAR_BLOCK_SIZE - remainder))
		}
	}

	chunks.push(new Uint8Array(TAR_BLOCK_SIZE))
	chunks.push(new Uint8Array(TAR_BLOCK_SIZE))

	return new Blob(chunks, { type: 'application/x-tar' })
}

function writeString(
	buffer: Uint8Array,
	offset: number,
	length: number,
	value: string,
) {
	const bytes = textEncoder.encode(value)
	const slice = bytes.slice(0, length)
	buffer.set(slice, offset)
}

function writeOctal(
	buffer: Uint8Array,
	offset: number,
	length: number,
	value: number,
) {
	const octal = value.toString(8)
	const padded = octal.padStart(length - 1, '0')
	writeString(buffer, offset, length, `${padded}\0`)
}
